import { useMemo, useRef } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSites } from 'store';

export interface IConnectedDevice {
  mac_address: string;
  upload: number;
  download: number;
  vlan: string;
  ssid: string;
  connected_since: string;
}

export interface IConnectedUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  upload: number;
  download: number;
  total_consumption: number;
  devices_count: number;
  connected_since: string;
  country: string | null;
  language: string | null;
  device: string | null;
  os_name: string | null;
  browser_name: string | null;
  devices: IConnectedDevice[];
}

const OPERATION_CONNECTED_USERS = gql`
  query ConnectedUsers(
    $site_sf_id: ID!
    $sort_by: String
    $sort_direction: String
    $offset: Int
    $limit: Int
    $search: String
  ) {
    connectedUsers(
      site_sf_id: $site_sf_id
      sort_by: $sort_by
      sort_direction: $sort_direction
      offset: $offset
      limit: $limit
      search: $search
    ) {
      users {
        id
        email
        first_name
        last_name
        upload
        download
        total_consumption
        devices_count
        connected_since
        country
        language
        device
        os_name
        browser_name
        devices {
          mac_address
          upload
          download
          vlan
          ssid
          connected_since
        }
      }
      total
    }
  }
`;

const PAGE_SIZE = 25;

interface UseConnectedUsersParams {
  sortBy?: string;
  sortDirection?: string;
  search?: string;
}

const useConnectedUsers = ({
  sortBy = 'connected_since',
  sortDirection = 'desc',
  search = ''
}: UseConnectedUsersParams = {}) => {
  const isLoadingMore = useRef<boolean>(false);
  const [{ selected: sitesSelected }] = useSites();

  const sfId: string = useMemo(
    () => sitesSelected[0]?.sf_id as string,
    [sitesSelected]
  );

  const queryVariables = useMemo(() => ({
    site_sf_id: sfId,
    sort_by: sortBy,
    sort_direction: sortDirection,
    search: search || null,
    offset: 0,
    limit: PAGE_SIZE
  }), [sfId, sortBy, sortDirection, search]);

  const { loading, data, error, fetchMore, networkStatus } = useQuery(OPERATION_CONNECTED_USERS, {
    notifyOnNetworkStatusChange: true,
    variables: queryVariables,
    skip: !sfId
  });

  const items = useMemo(() => data?.connectedUsers?.users || [], [data]);
  const total = useMemo(() => data?.connectedUsers?.total || 0, [data]);

  return {
    error: error?.message || null,
    loading: (loading && networkStatus !== 3) || networkStatus === 2,
    loadingNext: networkStatus === 3,
    shouldFetchNextPage: !loading && !isLoadingMore.current && items.length < total,
    fetchNextPage: async () => {
      if (networkStatus === 3 || isLoadingMore.current) return;
      try {
        isLoadingMore.current = true;
        await fetchMore({
          variables: { offset: items.length, limit: PAGE_SIZE },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            if (!prev.connectedUsers || !fetchMoreResult.connectedUsers) return prev;
            const existingIds = new Set(prev.connectedUsers.users.map(item => item.id));
            const newUsers = fetchMoreResult.connectedUsers.users.filter(
              item => !existingIds.has(item.id)
            );
            return {
              connectedUsers: {
                users: [...prev.connectedUsers.users, ...newUsers],
                total: fetchMoreResult.connectedUsers.total
              }
            };
          }
        });
      } catch (e) {
        console.error('Error fetching next page:', e);
      } finally {
        isLoadingMore.current = false;
      }
    },
    items: items as IConnectedUser[],
    total,
    realtimeCount: total
  };
};

export default useConnectedUsers;